import {Button, Collapse, Link, Stack, Typography} from "@mui/material";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import ArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import {Dialog} from "../base/Dialog";
import {useTranslation} from "react-i18next";
import {PropsWithChildren, useState} from "react";
import {EndUserLicenseAgreement} from "../documentation/EndUserLicenseAgreement";
import {PrivacyPolicy} from "../documentation/PrivacyPolicy";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
}

export const LicenseDialog: React.FC<Props> = ({open, onClose}) => {
    const {t} = useTranslation();

    return (
        <Dialog
            title={t("dialogs.license.title")}
            titleIcon={<InfoIcon color="primary" />}
            cancelTitle={t("dialogs.actions.close")}
            open={open}
            onClose={() => onClose(false)}
        >
            <Stack direction="column" gap="0.75rem">
                <FoldableDocument
                    title={t("dialogs.license.end_user_license_agreement")}
                    pdfLink="/legal/Terms_of_Service.pdf"
                    updatedAt="15/11/2024"
                >
                    <EndUserLicenseAgreement />
                </FoldableDocument>
                <FoldableDocument
                    title={t("dialogs.license.privacy_policy")}
                    pdfLink="/legal/Privacy_Policy.pdf"
                    updatedAt="15/11/2024"
                >
                    <PrivacyPolicy />
                </FoldableDocument>
            </Stack>
        </Dialog>
    );
};

interface FoldableDocumentProps extends PropsWithChildren {
    title: string;
    pdfLink: string;
    updatedAt: string;
}

const FoldableDocument: React.FC<FoldableDocumentProps> = ({title, pdfLink, updatedAt, children}) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState<boolean>(false);

    return (
        <Stack direction="column" sx={{border: 1, borderColor: "#E9EBF1"}}>
            <Button
                variant="text"
                onClick={() => setOpen(!open)}
                sx={{
                    padding: 0,
                    margin: 0,
                    textTransform: "none",
                    border: 0,
                }}
            >
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    paddingX="1rem"
                    alignItems="center"
                    height="3.5rem"
                    width="100%"
                    sx={{backgroundColor: "#E9EBF1", borderBottom: 1, borderColor: "#E9EBF1"}}
                >
                    <Stack direction="row" alignItems="center" gap="0.75rem">
                        {!open && <ArrowRightIcon sx={{color: "#404DB8"}} />}
                        {open && <ArrowDownIcon sx={{color: "#404DB8"}} />}
                        <Typography fontSize={16} fontWeight={500} color="#3A3F4F" textTransform="uppercase">
                            {title}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap="1rem">
                        <Typography fontSize={14} fontWeight={500} color="#737990">
                            {t("dialogs.license.updated_at", {date: updatedAt})}
                        </Typography>
                        <Button component={Link} href={pdfLink} target="_blank" rel="noreferrer">
                            <DownloadIcon sx={{color: "#303A8C"}} />
                            <Typography fontSize={16} fontWeight={500} color="#303A8C" marginLeft="0.25rem">
                                PDF
                            </Typography>
                        </Button>
                    </Stack>
                </Stack>
            </Button>
            <Collapse in={open} unmountOnExit>
                <Stack paddingX="3rem" paddingY="1.5rem">
                    {children}
                </Stack>
            </Collapse>
        </Stack>
    );
};
